/**
 * @quotech/ui - EmptyState
 * 
 * Unified empty state component for all Quotech hubs.
 * 
 * WHY: Provides consistent empty/error/no-results UX across hubs.
 *   - Type-based presets: 'no-data', 'no-results', 'error', 'empty'
 *   - Icon-based: pass any Lucide icon for custom states
 *   - Supports primary + secondary actions
 *   - Uses Lucide icons for preset types (simpler, consistent)
 * 
 * USAGE:
 *   // Preset type
 *   <EmptyState type="no-results" action={{ label: 'Clear Filters', onClick: onClear }} />
 * 
 *   // Custom icon
 *   <EmptyState icon={Users} title="No employees" message="Add your first employee." />
 * 
 *   // With actions
 *   <EmptyState
 *     type="no-data"
 *     action={{ label: 'Add Item', onClick: handleAdd, icon: Plus }}
 *     secondaryAction={{ label: 'Learn More', onClick: handleHelp }}
 *   />
 */

import { cn } from '../../lib/utils'
import {
  Inbox,
  Search,
  AlertCircle,
  Database,
  RefreshCw,
  Filter,
  Plus,
} from 'lucide-react'

// =============================================================================
// TYPES
// =============================================================================

interface EmptyStateProps {
  /** Preset type with default icon/title/message */
  type?: 'no-data' | 'no-results' | 'error' | 'empty'
  /** Custom Lucide icon (overrides type preset icon) */
  icon?: React.ElementType
  /** Title text (overrides type preset title) */
  title?: string
  /** Description message (overrides type preset message) */
  message?: string
  /** Primary action button */
  action?: {
    label: string
    onClick: () => void
    icon?: React.ElementType
  }
  /** Secondary action (text-style button) */
  secondaryAction?: {
    label: string
    onClick: () => void
  }
  /** Additional CSS classes for the container */
  className?: string
}

// =============================================================================
// PRESET CONFIGS
// =============================================================================

const presetConfigs = {
  'no-data': {
    icon: Database,
    title: 'No data available',
    message: 'Data will appear here once records are created or synced.',
    defaultAction: { label: 'Refresh', icon: RefreshCw },
  },
  'no-results': {
    icon: Search,
    title: 'No results found',
    message: 'Try adjusting your search or filter criteria.',
    defaultAction: { label: 'Clear Filters', icon: Filter },
  },
  'error': {
    icon: AlertCircle,
    title: 'Something went wrong',
    message: 'We encountered an error loading this data. Please try again.',
    defaultAction: { label: 'Retry', icon: RefreshCw },
  },
  'empty': {
    icon: Inbox,
    title: 'Nothing here yet',
    message: 'Get started by creating your first item.',
    defaultAction: { label: 'Create', icon: Plus },
  },
} as const

// =============================================================================
// COMPONENT
// =============================================================================

export function EmptyState({
  type,
  icon,
  title,
  message,
  action,
  secondaryAction,
  className,
}: EmptyStateProps) {
  // Resolve preset config (if type provided)
  const preset = type ? presetConfigs[type] : null

  // Determine icon, title, message (props override preset)
  const ResolvedIcon = icon || preset?.icon || Inbox
  const resolvedTitle = title || preset?.title || 'No data'
  const resolvedMessage = message || preset?.message
  const ActionIcon = action?.icon || preset?.defaultAction?.icon

  return (
    <div className={cn('flex flex-col items-center justify-center py-12 px-4', className)}>
      {/* Icon */}
      <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
        <ResolvedIcon className="w-8 h-8 text-slate-400 dark:text-slate-500" />
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
        {resolvedTitle}
      </h3>

      {/* Message */}
      {resolvedMessage && (
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 text-center max-w-sm">
          {resolvedMessage}
        </p>
      )}

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className="mt-6 flex items-center gap-3">
          {action && (
            <button
              onClick={action.onClick}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm',
                'bg-teal-600 text-white hover:bg-teal-500',
                'transition-colors'
              )}
            >
              {ActionIcon && <ActionIcon className="w-4 h-4" />}
              {action.label}
            </button>
          )}

          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className="px-4 py-2.5 text-sm text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default EmptyState
